import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '../utils/api'
import { CreditCard, User, Check } from 'lucide-react'

const TIERS = [
  { id: 'free', name: 'Free', price: '$0', features: ['5 SMS/month', '10 Emails/month', 'Basic scheduling'] },
  { id: 'email', name: 'Email Only', price: '$2.99', features: ['Unlimited emails', 'File attachments', 'Recurring messages'] },
  { id: 'starter', name: 'Starter SMS', price: '$4.99', features: ['100 SMS/month', 'Unlimited emails', 'File attachments'] },
  { id: 'pro', name: 'Pro SMS', price: '$14.99', features: ['500 SMS/month', 'Priority queue', 'Advanced analytics'] },
  { id: 'business', name: 'Business', price: '$29.99', features: ['1500 SMS/month', 'MMS support', 'Multi-recipient', 'Team access'] },
]

export default function Settings() {
  
  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: api.user.profile,
  })
  
  const checkoutMutation = useMutation({
    mutationFn: api.stripe.checkout,
    onSuccess: (data) => {
      if (data.url && data.url !== 'mock-checkout-url') {
        window.location.href = data.url
      }
    },
  })
  
  const portalMutation = useMutation({
    mutationFn: api.stripe.portal,
    onSuccess: (data) => {
      if (data.url && data.url !== 'mock-portal-url') {
        window.location.href = data.url
      }
    },
  })
  
  const currentTier = profile?.tier || 'free'
  const currentTierInfo = TIERS.find(t => t.id === currentTier)
  
  return (
    <div className="space-y-8 max-w-4xl">
      <h1 className="text-2xl font-bold">Settings</h1>
      
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <User className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-semibold">Profile</h2>
        </div>
        
        {isLoading ? (
          <div className="animate-pulse h-20 bg-gray-100 rounded-lg"></div>
        ) : (
          <div className="space-y-2">
            <p className="text-gray-600">Email: <span className="font-medium">{profile?.email}</span></p>
            <p className="text-gray-600">Member since: <span className="font-medium">{new Date(profile?.created_at).toLocaleDateString()}</span></p>
          </div>
        )}
      </div>
      
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-semibold">Subscription</h2>
        </div>
        
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-600">Current Plan</p>
          <p className="text-2xl font-bold">{currentTierInfo?.name}</p>
          <p className="text-gray-600">{currentTierInfo?.price}/month</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {TIERS.map((tier) => {
            const isCurrent = tier.id === currentTier
            return (
              <div
                key={tier.id}
                className={`p-4 border-2 rounded-xl ${
                  isCurrent
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold">{tier.name}</span>
                  {isCurrent && <Check className="w-5 h-5 text-blue-600" />}
                </div>
                <p className="text-xl font-bold mb-3">{tier.price}<span className="text-sm font-normal">/mo</span></p>
                <ul className="space-y-1 text-sm text-gray-600">
                  {tier.features.map((f, i) => (
                    <li key={i}>• {f}</li>
                  ))}
                </ul>
                {!isCurrent && (
                  <button
                    onClick={() => checkoutMutation.mutate(tier.id)}
                    disabled={checkoutMutation.isPending}
                    className="btn btn-primary w-full mt-4"
                  >
                    {checkoutMutation.isPending ? 'Loading...' : 'Upgrade'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
        
        {currentTier !== 'free' && (
          <div className="mt-6">
            <button
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
              className="btn btn-secondary"
            >
              {portalMutation.isPending ? 'Loading...' : 'Manage Billing'}
            </button>
          </div>
        )}
      </div>
      
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Usage This Month</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">SMS</p>
            <p className="text-xl font-bold">{profile?.sms_used || 0} / {profile?.sms_quota || 5}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">MMS</p>
            <p className="text-xl font-bold">{profile?.mms_used || 0} / {profile?.mms_quota || 0}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Emails</p>
            <p className="text-xl font-bold">{profile?.email_used || 0} {profile?.sms_quota === -1 ? '(Unlimited)' : ''}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
